import { Component, Input } from '@angular/core';

import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DigestService } from '../../services/digest.service';
import { AutocompleteChipsOptions } from '../../../../Common/Forms/components/AutocompleteChips/autocomplete-chips.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { Digest } from '@Monitor/Briefings/models/digest.model';
import { ManageDigestsDialogComponent } from '../ManageDigestDialog/manage-digests-dialog.component';
import { AppearCondition } from '@Common/Forms/components/Autocomplete/autocomplete-custom-option.model';

@Component({
    selector: 'agpr-digest-picker',
    templateUrl: './digest-picker.component.html',
    styleUrls: ['./digest-picker.component.scss'],
    providers: [getProvider(DigestPickerComponent)],
    standalone: false
})
export class DigestPickerComponent extends ControlValueAccessorModel<number[]> {

    @Input() placeholder: string = 'Briefings.deliveryOptions.digest.picker.placeholder';
    @Input() ariaDescribedBy: string;
    @Input() ariaInvalid: boolean;

    value: number[] = [];
    digests: Digest[] = [];

    // No value to indicate initial state
    private allDigests: Digest[];
    private subscriptions: Subscription[] = [];

    readonly autocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.autocompleteFetcher.bind(this),
        delay: 300,
        minLength: 3,
        dropdown: true,
        forceSelection: true,
        customOptions: [
            {
                label: 'Briefings.digest.actions.create',
                action: (digestName: string) => {
                    const newDigest = new Digest();
                    newDigest.name = digestName;

                    this.digestService.create(newDigest).subscribe((id) => {
                        this.value.push(id);
                    });
                },
                appearCondition: AppearCondition.noExactMatchInSuggestions,
                clearOnSelect: true
            }
        ]
    };

    constructor(
        private coreDialogService: CoreDialogService,
        private digestService: DigestService,
    ) {
        super();

        this.subscriptions.push(
            this.digestService.all$.subscribe(digests => {
                this.allDigests = digests || [];
                this.getDigestsByIds(this.allDigests);
            })
        );
    }

    writeValue(value: number[]): void {
        super.writeValue(value || []);
        this.getDigestsByIds(this.allDigests);
    }

    getDigestsByIds(allDigests: Digest[]) {
        if (!allDigests) {

            return;
        }

        this.digests = this.value
            .map(id => allDigests.find(digest => digest.id === id))
            .filter(digest => !!digest);

        if (this.value.length !== this.digests.length) {
            // if a digest was deleted, then remove it from the list of digests
            this.updateDigests();
        }
    }

    updateDigests() {
        if (this.allDigests) {
            this.value = this.digests.map(digest => digest.id);
            this.onModelChange(this.value);
        }
    }

    autocompleteFetcher(query: string): Observable<Digest[]> {
        if (!query) {
            return of(this.allDigests.slice());
        } else {
            return of(this.allDigests?.filter(digest => digest.name.toLocaleLowerCase().includes(query.toLocaleLowerCase())));
        }
    }

    openManage() {
        // close the dropdown before opening the dialog to prevent it from being stuck open in the background
        document.dispatchEvent(new MouseEvent('click'));
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageDigestsDialogComponent, io);
    }

    clearAllChips(allChipsRemoved: boolean): void {
        if (allChipsRemoved) {
            this.digests?.splice(0, this.digests?.length);
            this.updateDigests();
        }
    }
}
